import React, { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import type { ReleaseThemeId } from 'fym-shared/src/music24/releaseThemes';
import { useUpdateKitLookMutation } from '../../../redux/api/fymAPI';
import { TypedMemo } from '../../common/types';
import { SMLButton } from '../../common/views/SMLButton/SMLButton';
import { ThemeTiles } from '../../music24/promote/releases/theme/ThemeTiles';
import { coverEditorMessages } from '../coverEditorMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the cover editor; each part uses its own slice of it */
import styles from '../CoverEditor.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface CoverThemeStepProps {
  accountId: number;
  catalogItemUuid: string;
  title: string;
  /** The release theme of the song now: the first selected tile. */
  themeId: ReleaseThemeId;
  /** The pictures of the welcome run (4). */
  pictureCount: number;
  /** Called after the theme is saved: the editor reads the song again and opens. */
  onDone: () => Promise<unknown>;
  onBack: () => void;
}

/**
 * Step 1 of the first open of a trial workspace (owner request 2026-10-09): the user picks the
 * release theme of the song, then the editor opens and AI makes the 4 welcome pictures in that
 * theme. The theme is the release theme of the song (the 8 tiles of the Theme panel), the same
 * theme that the bank filter, the prompt and the kit use. A pick saves the look of the song kit
 * (`PATCH .../look`). No Claude Design frame shows this step yet: it uses the Theme panel tiles and
 * the tokens of the cover editor.
 */
const CoverThemeStepComponent: React.FC<CoverThemeStepProps> = ({
  accountId,
  catalogItemUuid,
  title,
  themeId,
  pictureCount,
  onDone,
  onBack,
}) => {
  const intl = useIntl();
  const [picked, setPicked] = useState<ReleaseThemeId>(themeId);
  const [updateLook] = useUpdateKitLookMutation();
  const [isSaving, setIsSaving] = useState(false);
  const [isFailed, setIsFailed] = useState(false);

  const pick = useCallback((next: ReleaseThemeId) => {
    setPicked(next);
    setIsFailed(false);
  }, []);

  const confirm = useCallback(() => {
    setIsSaving(true);
    setIsFailed(false);
    const save = async (): Promise<void> => {
      if (picked !== themeId) {
        await updateLook({ accountId, catalogItemUuid, themeId: picked }).unwrap();
      }
      await onDone();
    };
    save()
      .catch((error: unknown) => {
        console.error('Cover editor: the theme of the first open was not saved', error);
        setIsFailed(true);
      })
      .finally(() => setIsSaving(false));
  }, [accountId, catalogItemUuid, onDone, picked, themeId, updateLook]);

  return (
    <div className={styles.themeStep}>
      <div className={styles.themeStepHead}>
        <button type="button" className={styles.linkButton} onClick={onBack}>
          {intl.formatMessage(coverEditorMessages.back)}
        </button>
        <span className={styles.eyebrow}>{intl.formatMessage(coverEditorMessages.themeStepEyebrow)}</span>
        <h1 className={styles.themeStepTitle}>{intl.formatMessage(coverEditorMessages.themeStepTitle)}</h1>
        <p className={styles.fullStateText}>
          {intl.formatMessage(coverEditorMessages.themeStepText, { count: pictureCount, title })}
        </p>
      </div>
      <ThemeTiles value={picked} onChange={pick} />
      <div className={styles.themeStepFoot}>
        {isFailed && (
          <span className={styles.errorText} role="alert">
            {intl.formatMessage(coverEditorMessages.themeStepFailed)}
          </span>
        )}
        <SMLButton omitCap onClick={confirm} isLoading={isSaving} disabled={isSaving}>
          {intl.formatMessage(coverEditorMessages.themeStepContinue, { count: pictureCount })}
        </SMLButton>
      </div>
    </div>
  );
};

export const CoverThemeStep = TypedMemo(CoverThemeStepComponent);
